import { useState } from 'react';
import { HexColorPicker } from 'react-colorful';
import { useEditorStore } from '../../store/editorStore';
import { loadGoogleFont } from '../../lib/googleFonts';

const FONT_FAMILIES = [
  'Inter', 'Georgia', 'Times New Roman', 'Arial', 'Helvetica',
  'Roboto', 'Montserrat', 'Playfair Display', 'Courier New',
];

const FONT_WEIGHTS = [
  { value: '300', label: 'Light' },
  { value: '400', label: 'Regular' },
  { value: '500', label: 'Medium' },
  { value: '600', label: 'SemiBold' },
  { value: '700', label: 'Bold' },
  { value: '800', label: 'ExtraBold' },
  { value: '900', label: 'Black' },
];

export default function TextProperties({ layer }) {
  const data = layer.data;
  const style = data.style;
  const updateLayerData = useEditorStore((s) => s.updateLayerData);
  const [colorPickerFor, setColorPickerFor] = useState(null);

  function updateStyle(updates) {
    updateLayerData(layer.id, {
      style: { ...style, ...updates },
    });
  }

  function updateContent(content) {
    updateLayerData(layer.id, { content });
  }

  return (
    <div className="p-2 space-y-3">
      <p className="panel-label">Text</p>

      {/* Content */}
      <div>
        <label className="text-xs block mb-0.5" style={{ color: 'var(--t-muted)' }}>Content</label>
        <textarea
          value={data.content}
          onChange={(e) => updateContent(e.target.value)}
          className="input-field resize-none text-xs"
          rows={3}
        />
      </div>

      {/* Font */}
      <div className="grid grid-cols-2 gap-1.5">
        <div>
          <label className="text-xs block mb-0.5" style={{ color: 'var(--t-muted)' }}>Font</label>
          <select
            value={style.fontFamily}
            onChange={(e) => { loadGoogleFont(e.target.value); updateStyle({ fontFamily: e.target.value }); }}
            className="input-field text-xs"
          >
            {FONT_FAMILIES.map((f) => (
              <option key={f} value={f}>{f}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs block mb-0.5" style={{ color: 'var(--t-muted)' }}>Size</label>
          <input
            type="number"
            value={style.fontSize}
            onChange={(e) => updateStyle({ fontSize: parseInt(e.target.value) || 16 })}
            min={8}
            max={200}
            className="input-field text-xs py-1"
          />
        </div>
      </div>

      {/* Weight & Transform */}
      <div className="grid grid-cols-2 gap-1.5">
        <div>
          <label className="text-xs block mb-0.5" style={{ color: 'var(--t-muted)' }}>Weight</label>
          <select
            value={style.fontWeight}
            onChange={(e) => updateStyle({ fontWeight: e.target.value })}
            className="input-field text-xs"
          >
            {FONT_WEIGHTS.map((w) => (
              <option key={w.value} value={w.value}>{w.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs block mb-0.5" style={{ color: 'var(--t-muted)' }}>Transform</label>
          <select
            value={style.textTransform}
            onChange={(e) => updateStyle({ textTransform: e.target.value })}
            className="input-field text-xs"
          >
            <option value="none">None</option>
            <option value="uppercase">UPPERCASE</option>
            <option value="lowercase">lowercase</option>
            <option value="capitalize">Capitalize</option>
          </select>
        </div>
      </div>

      {/* Alignment */}
      <div>
        <label className="text-xs block mb-1" style={{ color: 'var(--t-muted)' }}>Alignment</label>
        <div className="flex gap-1">
          {['left', 'center', 'right', 'justify'].map((align) => (
            <button
              key={align}
              onClick={() => updateStyle({ textAlign: align })}
              className={`flex-1 py-1 rounded text-xs capitalize transition-colors
                ${style.textAlign === align ? 'bg-indigo-600 text-white' : ''}`}
              style={style.textAlign !== align ? { background: 'var(--t-panel)', color: 'var(--t-muted)' } : {}}
            >
              {align === 'left' ? '⬅' : align === 'center' ? '↔' : align === 'right' ? '➡' : '≡'}
            </button>
          ))}
        </div>
      </div>

      {/* Spacing */}
      <div className="grid grid-cols-2 gap-1.5">
        <div>
          <label className="text-xs block mb-0.5" style={{ color: 'var(--t-muted)' }}>Line Height</label>
          <input
            type="number"
            value={style.lineHeight}
            onChange={(e) => updateStyle({ lineHeight: parseFloat(e.target.value) || 1.2 })}
            step={0.1}
            min={0.5}
            max={4}
            className="input-field text-xs py-1"
          />
        </div>
        <div>
          <label className="text-xs block mb-0.5" style={{ color: 'var(--t-muted)' }}>Letter Spacing</label>
          <input
            type="number"
            value={style.letterSpacing}
            onChange={(e) => updateStyle({ letterSpacing: parseFloat(e.target.value) || 0 })}
            step={0.5}
            min={-5}
            max={20}
            className="input-field text-xs py-1"
          />
        </div>
      </div>

      {/* Text color */}
      <div>
        <label className="text-xs block mb-1" style={{ color: 'var(--t-muted)' }}>Text Color</label>
        <div className="flex items-center gap-2">
          <div
            className="w-7 h-7 rounded cursor-pointer flex-shrink-0"
            style={{ border: '1px solid var(--t-border2)', background: style.color }}
            onClick={() => setColorPickerFor(colorPickerFor === 'color' ? null : 'color')}
          />
          <input
            type="text"
            value={style.color}
            onChange={(e) => updateStyle({ color: e.target.value })}
            className="input-field text-xs py-1 flex-1 font-mono"
          />
        </div>
        {colorPickerFor === 'color' && (
          <div className="mt-2">
            <HexColorPicker
              color={style.color}
              onChange={(c) => updateStyle({ color: c })}
              style={{ width: '100%', height: 120 }}
            />
          </div>
        )}
      </div>

      {/* Background color */}
      <div>
        <label className="text-xs block mb-1" style={{ color: 'var(--t-muted)' }}>Background Color</label>
        <div className="flex items-center gap-2">
          <div
            className="w-7 h-7 rounded cursor-pointer flex-shrink-0"
            style={{ border: '1px solid var(--t-border2)', background: style.backgroundColor || 'transparent' }}
            onClick={() => setColorPickerFor(colorPickerFor === 'bg' ? null : 'bg')}
          />
          <input
            type="text"
            value={style.backgroundColor || ''}
            onChange={(e) => updateStyle({ backgroundColor: e.target.value || null })}
            className="input-field text-xs py-1 flex-1 font-mono"
            placeholder="none"
          />
          {style.backgroundColor && (
            <button
              onClick={() => updateStyle({ backgroundColor: null })}
              className="text-xs hover:text-gray-300 transition-colors"
              style={{ color: 'var(--t-muted)' }}
            >
              ✕
            </button>
          )}
        </div>
        {colorPickerFor === 'bg' && (
          <div className="mt-2">
            <HexColorPicker
              color={style.backgroundColor || '#000000'}
              onChange={(c) => updateStyle({ backgroundColor: c })}
              style={{ width: '100%', height: 120 }}
            />
          </div>
        )}
      </div>

      {/* Border radius */}
      <div>
        <label className="text-xs block mb-0.5" style={{ color: 'var(--t-muted)' }}>Border Radius</label>
        <input
          type="range"
          value={style.borderRadius}
          onChange={(e) => updateStyle({ borderRadius: parseInt(e.target.value) })}
          min={0}
          max={50}
          className="w-full"
        />
        <span className="text-xs" style={{ color: 'var(--t-muted)' }}>{style.borderRadius}px</span>
      </div>
    </div>
  );
}
